import { useState, useEffect, useRef } from 'react';

const PICKER_SELECTOR = '[data-emoji-picker-container]';

/**
 * Detects when emoji picker popup appears/disappears.
 * Uses a MutationObserver that disconnects when picker is found
 * and uses RAF-based polling while open, then reconnects when it closes.
 */
export const useEmojiPickerDetection = (): boolean => {
	const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
	const debounceFrameIdRef = useRef<number | null>(null);
	const pollFrameIdRef = useRef<number | null>(null);
	const observerRef = useRef<MutationObserver | null>(null);
	const isOpenRef = useRef(false);

	useEffect(() => {
		if (typeof document === 'undefined' || typeof window === 'undefined') {
			return;
		}

		let idleCallbackId: number | ReturnType<typeof setTimeout> | null = null;
		let isMounted = true;

		const checkPickerOpen = (): boolean => {
			const pickerElement = document.querySelector(PICKER_SELECTOR);
			return pickerElement !== null;
		};

		const updateState = (isOpen: boolean) => {
			// Only update state if component is still mounted and state actually changed
			if (!isMounted) {
				return;
			}
			if (isOpenRef.current !== isOpen) {
				isOpenRef.current = isOpen;
				setIsEmojiPickerOpen(isOpen);
			}
		};

		const startPolling = () => {
			if (pollFrameIdRef.current !== null) {
				return; // Already polling
			}

			const poll = () => {
				// Guard: don't continue if component unmounted
				if (!isMounted || pollFrameIdRef.current === null) {
					return;
				}

				const stillOpen = checkPickerOpen();
				updateState(stillOpen);

				if (!stillOpen) {
					// Picker closed, stop polling and reconnect observer
					pollFrameIdRef.current = null;
					if (isMounted && observerRef.current) {
						observerRef.current.observe(document.body, {
							childList: true,
							subtree: true,
						});
					}
				} else if (isMounted) {
					pollFrameIdRef.current = window.requestAnimationFrame(poll);
				}
			};

			pollFrameIdRef.current = window.requestAnimationFrame(poll);
		};

		const stopPolling = () => {
			if (pollFrameIdRef.current !== null) {
				window.cancelAnimationFrame(pollFrameIdRef.current);
				pollFrameIdRef.current = null;
			}
		};

		const debouncedCheck = () => {
			if (debounceFrameIdRef.current === null) {
				debounceFrameIdRef.current = window.requestAnimationFrame(() => {
					// Guard: don't process if component unmounted
					if (!isMounted) {
						debounceFrameIdRef.current = null;
						return;
					}

					const isOpen = checkPickerOpen();
					updateState(isOpen);

					// If picker just opened and we're observing, switch to polling
					if (isOpen && isMounted && observerRef.current && pollFrameIdRef.current === null) {
						observerRef.current.disconnect();
						startPolling();
					}

					debounceFrameIdRef.current = null;
				});
			}
		};

		const setupObserver = () => {
			observerRef.current = new MutationObserver((mutations) => {
				const hasRelevantChanges = mutations.some((mutation) => {
					if (mutation.type !== 'childList') {
						return false;
					}
					return mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0;
				});

				if (hasRelevantChanges) {
					debouncedCheck();
				}
			});

			const isOpen = checkPickerOpen();
			updateState(isOpen);

			if (isOpen) {
				startPolling();
			} else {
				observerRef.current.observe(document.body, {
					childList: true,
					subtree: true,
				});
			}
		};

		if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
			idleCallbackId = (
				window as Window & {
					requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number;
				}
			).requestIdleCallback(() => setupObserver(), { timeout: 200 });
		} else {
			idleCallbackId = setTimeout(setupObserver, 50);
		}

		return () => {
			isMounted = false;
			if (debounceFrameIdRef.current !== null) {
				window.cancelAnimationFrame(debounceFrameIdRef.current);
				debounceFrameIdRef.current = null;
			}
			stopPolling();
			if (observerRef.current) {
				observerRef.current.disconnect();
				observerRef.current = null;
			}
			if (idleCallbackId !== null) {
				if (
					typeof window !== 'undefined' &&
					'cancelIdleCallback' in window &&
					typeof idleCallbackId === 'number'
				) {
					(window as Window & { cancelIdleCallback: (id: number) => void }).cancelIdleCallback(
						idleCallbackId,
					);
				} else {
					clearTimeout(idleCallbackId as ReturnType<typeof setTimeout>);
				}
			}
		};
	}, []);

	return isEmojiPickerOpen;
};
